// @owners { team: patients-team }
import { ActionSheetContext } from '@alto/design-system';
import React, { useContext } from 'react';
import {
  setOnboardingVerificationChannel,
  setOnboardingVerificationRequired,
  // eslint-disable-next-line import/no-deprecated
  startVerification,
} from '~shared/actions/verifications';
import {
  getOnboardingVerificationOptions,
  getOnboardingVerificationUuid,
} from '~shared/features/onboarding/selectors/getOnboardingVerification';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { VerificationBlockedActionSheet } from '~web/features/onboarding/components/VerificationBlockedActionSheet';
import {
  type Values,
  VerificationOptionsActionSheet,
} from '~web/features/onboarding/components/VerificationOptionsActionSheet';
import { VerificationSingleOptionActionSheet } from '~web/features/onboarding/components/VerificationSingleOptionActionSheet';

type Props = {
  readonly resend?: boolean;
};

export const VerificationActionSheet = ({ resend = false }: Props) => {
  const dispatch = useDispatchShared();
  const { closeActionSheet } = useContext(ActionSheetContext);
  const uuid = useSelectorShared(getOnboardingVerificationUuid);
  const options = useSelectorShared(getOnboardingVerificationOptions);
  const loading = useSelectorShared((state) => state.ui.loading.startVerificationLoading);
  const error = useSelectorShared((state) => state.ui.errors.startVerificationError);

  const onSubmit = async ({ channel: comms_channel }: Values) => {
    const success = await dispatch(
      // eslint-disable-next-line import/no-deprecated
      startVerification({
        uuid,
        reason: 'duplicate_account',
        comms_channel,
      }),
    );

    if (success) {
      closeActionSheet();
      dispatch(setOnboardingVerificationRequired());
      dispatch(setOnboardingVerificationChannel(comms_channel));
    }
  };

  if (options.length === 0) {
    return <VerificationBlockedActionSheet />;
  }

  if (options.length === 1) {
    const option = options[0];
    return (
      <VerificationSingleOptionActionSheet
        error={error}
        loading={loading}
        onSubmit={onSubmit}
        option={option}
        resend={resend}
      />
    );
  }

  return (
    <VerificationOptionsActionSheet
      error={error}
      loading={loading}
      onSubmit={onSubmit}
      options={options}
      resend={resend}
    />
  );
};
